import { ReportData, SourcePerformance, AdSpendSource, SalesSource, TradeSales, JobData, ReportPeriod } from '../types/reportTypes';
import { januaryData } from './januaryData';
import { februaryData } from './februaryData';
import { sampleData as marchData } from './sampleData'; // March data is in sampleData.ts

// Helper function to get month name
const getMonthName = (month: number): string => {
  return new Date(2000, month - 1).toLocaleString('default', { month: 'long' });
};

// Helper function to merge and de-duplicate sources by name
const mergeSourcesByName = <T extends { source: string }>(
  arrays: T[][],
  propertyExtractor: (item: T) => { [key: string]: number }
): T[] => {
  const sourceMap = new Map<string, { source: string } & { [key: string]: number }>();

  arrays.forEach(array => {
    array.forEach(item => {
      const sourceName = item.source;
      const extracted = propertyExtractor(item);
      
      if (!sourceMap.has(sourceName)) {
        const initialObject = { source: sourceName } as { source: string } & { [key: string]: number };
        
        // Add properties with initial value of 0
        Object.keys(extracted).forEach(key => {
          initialObject[key] = 0;
        });
        
        sourceMap.set(sourceName, initialObject);
      }
      
      const existing = sourceMap.get(sourceName)!;
      
      // Add values from current item to existing totals
      Object.entries(extracted).forEach(([key, value]) => {
        existing[key] = (existing[key] || 0) + value;
      });
    });
  });

  return Array.from(sourceMap.values()) as unknown as T[];
};

// Aggregate KPI data from all three months
const aggregateKPIs = () => {
  return {
    adSpend: januaryData.kpis.adSpend + februaryData.kpis.adSpend + marchData.kpis.adSpend,
    totalSales: januaryData.kpis.totalSales + februaryData.kpis.totalSales + marchData.kpis.totalSales,
    grossProfit: januaryData.kpis.grossProfit + februaryData.kpis.grossProfit + marchData.kpis.grossProfit,
    salesCommissions: januaryData.kpis.salesCommissions + februaryData.kpis.salesCommissions + marchData.kpis.salesCommissions,
    managerFees: januaryData.kpis.managerFees + februaryData.kpis.managerFees + marchData.kpis.managerFees,
    companyOH: januaryData.kpis.companyOH + februaryData.kpis.companyOH + marchData.kpis.companyOH,
    netProfit: januaryData.kpis.netProfit + februaryData.kpis.netProfit + marchData.kpis.netProfit,
    // Calculate net profit percentage over the entire quarter
    netProfitPercent: (januaryData.kpis.netProfit + februaryData.kpis.netProfit + marchData.kpis.netProfit) / 
                      (januaryData.kpis.totalSales + februaryData.kpis.totalSales + marchData.kpis.totalSales),
    // Calculate gross profit percentage over the entire quarter
    grossProfitPercent: (januaryData.kpis.grossProfit + februaryData.kpis.grossProfit + marchData.kpis.grossProfit) / 
                        (januaryData.kpis.totalSales + februaryData.kpis.totalSales + marchData.kpis.totalSales)
  };
};

// Combine ad spend data from all three months
const aggregateAdSpendBySource = (): AdSpendSource[] => {
  return mergeSourcesByName<AdSpendSource>(
    [januaryData.adSpendBySource, februaryData.adSpendBySource, marchData.adSpendBySource],
    (item) => ({ spend: item.spend })
  ).sort((a, b) => b.spend - a.spend);
};

// Combine sales data by source from all three months
const aggregateSalesBySource = (): SalesSource[] => {
  return mergeSourcesByName<SalesSource>(
    [januaryData.salesBySource, februaryData.salesBySource, marchData.salesBySource],
    (item) => ({ totalSales: item.totalSales, count: item.count, grossProfit: item.grossProfit })
  ).sort((a, b) => b.totalSales - a.totalSales);
};

// Combine sales data by trade from all three months
const aggregateSalesByTrade = (): TradeSales[] => {
  // Create a map to store aggregated trade data
  const tradeMap = new Map<string, TradeSales>();
  
  // Process each month's data
  [januaryData.salesByTrade, februaryData.salesByTrade, marchData.salesByTrade].forEach(monthData => {
    monthData.forEach(tradeItem => {
      const tradeName = tradeItem.trade;
      
      if (!tradeMap.has(tradeName)) {
        tradeMap.set(tradeName, {
          trade: tradeName,
          totalSales: 0,
          count: 0,
          grossProfit: 0
        });
      }
      
      const existing = tradeMap.get(tradeName)!;
      existing.totalSales += tradeItem.totalSales;
      existing.count += tradeItem.count;
      existing.grossProfit += tradeItem.grossProfit;
    });
  });
  
  return Array.from(tradeMap.values()).sort((a, b) => b.totalSales - a.totalSales);
};

// Combine job data from all three months with month indicators
const aggregateJobData = (): JobData[] => {
  const januaryJobs = januaryData.jobData.map(job => ({
    ...job,
    jobInfo: `${job.jobInfo} (Jan)`
  }));
  
  const februaryJobs = februaryData.jobData.map(job => ({
    ...job,
    jobInfo: `${job.jobInfo} (Feb)`
  }));
  
  const marchJobs = marchData.jobData.map(job => ({
    ...job,
    jobInfo: `${job.jobInfo} (Mar)`
  }));
  
  return [...januaryJobs, ...februaryJobs, ...marchJobs];
};

// Calculate month-to-month trend data for key metrics
const monthlyTrendData = [
  {
    month: 'January',
    totalSales: januaryData.kpis.totalSales,
    adSpend: januaryData.kpis.adSpend,
    grossProfit: januaryData.kpis.grossProfit,
    netProfit: januaryData.kpis.netProfit,
    salesCount: januaryData.jobData.length,
    avgSaleValue: januaryData.kpis.totalSales / januaryData.jobData.length,
    roi: januaryData.kpis.grossProfit / januaryData.kpis.adSpend,
    grossMarginPct: januaryData.kpis.grossProfitPercent,
    netMarginPct: januaryData.kpis.netProfitPercent
  },
  {
    month: 'February',
    totalSales: februaryData.kpis.totalSales,
    adSpend: februaryData.kpis.adSpend,
    grossProfit: februaryData.kpis.grossProfit,
    netProfit: februaryData.kpis.netProfit,
    salesCount: februaryData.jobData.length,
    avgSaleValue: februaryData.kpis.totalSales / februaryData.jobData.length,
    roi: februaryData.kpis.grossProfit / februaryData.kpis.adSpend,
    grossMarginPct: februaryData.kpis.grossProfitPercent,
    netMarginPct: februaryData.kpis.netProfitPercent
  },
  {
    month: 'March',
    totalSales: marchData.kpis.totalSales,
    adSpend: marchData.kpis.adSpend,
    grossProfit: marchData.kpis.grossProfit,
    netProfit: marchData.kpis.netProfit,
    salesCount: marchData.jobData.length,
    avgSaleValue: marchData.kpis.totalSales / marchData.jobData.length,
    roi: marchData.kpis.grossProfit / marchData.kpis.adSpend,
    grossMarginPct: marchData.kpis.grossProfitPercent,
    netMarginPct: marchData.kpis.netProfitPercent
  }
];

// Calculate performance trend by lead source across months
const leadSourcePerformanceTrend = () => {
  const sources = new Set<string>();
  
  // Collect all unique lead sources
  januaryData.salesBySource.forEach(item => sources.add(item.source));
  februaryData.salesBySource.forEach(item => sources.add(item.source));
  marchData.salesBySource.forEach(item => sources.add(item.source));
  
  // For each source, collect monthly data if available
  return Array.from(sources).map(source => {
    const janSource = januaryData.salesBySource.find(item => item.source === source);
    const febSource = februaryData.salesBySource.find(item => item.source === source);
    const marSource = marchData.salesBySource.find(item => item.source === source);
    
    const janAdSpend = januaryData.adSpendBySource.find(item => item.source === source)?.spend || 0;
    const febAdSpend = februaryData.adSpendBySource.find(item => item.source === source)?.spend || 0;
    const marAdSpend = marchData.adSpendBySource.find(item => item.source === source)?.spend || 0;
    
    return {
      source,
      january: {
        sales: janSource?.totalSales || 0,
        count: janSource?.count || 0,
        adSpend: janAdSpend,
        roi: janSource ? janSource.grossProfit / (janAdSpend || 1) : 0
      },
      february: {
        sales: febSource?.totalSales || 0,
        count: febSource?.count || 0,
        adSpend: febAdSpend,
        roi: febSource ? febSource.grossProfit / (febAdSpend || 1) : 0
      },
      march: {
        sales: marSource?.totalSales || 0,
        count: marSource?.count || 0,
        adSpend: marAdSpend,
        roi: marSource ? marSource.grossProfit / (marAdSpend || 1) : 0
      },
      q1Total: {
        sales: (janSource?.totalSales || 0) + (febSource?.totalSales || 0) + (marSource?.totalSales || 0),
        count: (janSource?.count || 0) + (febSource?.count || 0) + (marSource?.count || 0),
        adSpend: janAdSpend + febAdSpend + marAdSpend,
        grossProfit: (janSource?.grossProfit || 0) + (febSource?.grossProfit || 0) + (marSource?.grossProfit || 0)
      }
    };
  }).filter(item => item.q1Total.sales > 0) // Only include sources with sales
    .sort((a, b) => b.q1Total.sales - a.q1Total.sales); // Sort by total sales
};

// Calculate combined source performance with ROI metrics
const calculateSourcePerformance = (): SourcePerformance[] => {
  const salesBySource = aggregateSalesBySource();
  const adSpendBySource = aggregateAdSpendBySource();
  
  // Import standardizeSourceName function directly to ensure it's available
  const standardizeSourceName = (sourceName: string): string => {
    const sourceMap: Record<string, string> = {
      // Ad spend sources to standardized names
      "contractor appointment": "contractors appointments",
      "google adwords": "google paid search",
      "google lsa": "google local service ads", 
      "web to lead form (roofing calculator)": "web to lead form",
      "facebook / meta ads": "facebook/meta ads",
      "facebook/ meta ads": "facebook/meta ads",
      "facebook/meta ads": "facebook/meta ads",
      
      // Sales sources to standardized names
      "contractors appointments": "contractors appointments",
      "google paid search": "google paid search",
      "google organic search": "google organic",
      "google organic": "google organic",
      "web to lead form": "web to lead form",
      "networx": "networx",
      "thumbtack": "thumbtack",
      "homebuddy": "homebuddy",
      "suburban md spring home show": "suburban md spring home show",
      "yard sign": "yard sign",
      "referral": "referral",
      "referral - word of mouth": "referral - word of mouth",
      "yelp": "yelp",
      "angi": "angi",
      "previous customer": "previous customer"
    };
    
    // Convert to lowercase for case-insensitive comparison
    const lowercaseSource = sourceName.toLowerCase();
    
    // Return the standardized name if it exists, otherwise return the original name
    return sourceMap[lowercaseSource] || lowercaseSource;
  };
  
  return salesBySource.map(salesItem => {
    // Standardize the sales source name
    const standardizedSalesSource = standardizeSourceName(salesItem.source);
    
    // Find matching ad spend using standardized names
    const adSpendItem = adSpendBySource.find(
      item => standardizeSourceName(item.source) === standardizedSalesSource
    );
    
    const adSpend = adSpendItem ? adSpendItem.spend : 0;
    
    return {
      source: salesItem.source,
      totalSales: salesItem.totalSales,
      count: salesItem.count,
      grossProfit: salesItem.grossProfit,
      adSpend: adSpend,
      roi: salesItem.grossProfit / (adSpend || 1), // Avoid division by zero
      costPerSale: adSpend / (salesItem.count || 1), // Avoid division by zero
      costPerDollar: adSpend / (salesItem.totalSales || 1) // Avoid division by zero
    };
  }).sort((a, b) => b.roi - a.roi); // Sort by ROI from highest to lowest
};

// Set the report period to Q1 2025
const period: ReportPeriod = {
  year: 2025,
  month: 3, // Use the last month of the quarter as the reference month
  quarter: 1,
  startMonth: 1,
  endMonth: 3
};

// Q1 2025 consolidated data for the marketing report
export const q1Data: ReportData = {
  kpis: aggregateKPIs(),
  adSpendBySource: aggregateAdSpendBySource(),
  salesBySource: aggregateSalesBySource(),
  salesByTrade: aggregateSalesByTrade(),
  jobData: aggregateJobData(),
  period: period
};

// Export additional data for quarterly analysis
export const q1TrendData = {
  monthlyTrends: monthlyTrendData,
  sourcePerformance: calculateSourcePerformance(),
  leadSourceTrends: leadSourcePerformanceTrend(),
};